<template>
  <div>
    <custom-card v-if="uniqueStreamers.length === 0">
      <template v-slot:title>{{ $t('multi-stream.title') }}</template>
      <template v-slot:content>
        <div v-if="uniqueStreamers.length === 0">
          <v-sheet color="dark-background">
            <p class="mb-10">
              <i class="info">{{ $t('multi-stream.description') }}</i>
            </p>
            <v-form @submit.prevent="addStreamer">
              <v-row>
                <v-col>
                  <kicklet-text-field
                      v-model="name"
                      label="Streamer"
                  ></kicklet-text-field>
                  <kicklet-button
                      block
                      class="mt-2"
                      text="Add"
                      :variant="utils.buttonVariant"
                      color="primary"
                      @click="addStreamer"
                  />
                  <kicklet-button
                      block
                      class="mt-2"
                      text="Open Link"
                      color="primary"
                      @click="generateLink"
                      :disabled="streamerNames.length === 0"
                      variant="outlined"
                  />
                </v-col>
                <v-col>
                  <v-card class="pb-5" title="Streamers">
                    <v-list class="pt-0" v-if="streamerNames.length !== 0">
                      <div v-for="(streamer, index) in streamerNames" :key="index">
                        <v-list-item>
                          <v-list-item-title>{{ streamer }}</v-list-item-title>
                          <template v-slot:append>
                            <v-icon color="red" @click="removeStreamer(index)">mdi-close</v-icon>
                          </template>
                        </v-list-item>
                        <v-divider />
                      </div>
                    </v-list>
                    <v-card-subtitle v-else>No Streamers added</v-card-subtitle>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-sheet>
        </div>
      </template>
    </custom-card>
    <div class="streams-wrapper pa-2" v-if="uniqueStreamers.length > 0">
      <div class="stream-item" v-for="(name, i) in uniqueStreamers" :key="i">
        <v-card elevation="10" color="dark-background" class="stream-wrapper">
          <div class="stream-frame-wrapper">
            <iframe
                :src="'https://player.kick.com/' + name + '?autoplay=true'"
                width="100%"
                height="100%"
                class="stream-frame"
                frameborder="0"
                scrolling="no"
                allowfullscreen="true"
            ></iframe>
          </div>
          <div id="chat-wrapper" class="chat-frame-wrapper">
            <h2 class="text-center">Chat</h2>
            <v-divider class="mb-2" />
            <div class="chat-frame">
              <kick-chat :streamer="name" />
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Analytics } from '@/services/Analytics';
import KickChat from '@/components/KickChat';
import CustomCard from '@/components/custom/CustomCard.vue';
import KickletButton from '@/components/custom/kicklet/KickletButton.vue';
import KickletTextField from '@/components/custom/kicklet/KickletTextField.vue';

const router = useRouter();
const route = useRoute();
const streamerNames = ref([]);
const name = ref(null);

const streamers = computed(() => {
  if (Array.isArray(route.query.name)) {
    return route.query.name;
  } else if (typeof route.query.name === 'string') {
    return [route.query.name];
  } else {
    return [];
  }
});

const uniqueStreamers = computed(() => {
  const streamerSet = new Set(streamers.value);
  return Array.from(streamerSet);
});

const dimensions = computed(() => {
  const x = uniqueStreamers.value.length;
  const n = Math.ceil(Math.sqrt(x));

  let rows;
  let cols;

  if (n * (n - 1) >= x) {
    rows = n - 1;
    cols = n;
  } else {
    rows = n;
    cols = n;
  }

  return {
    rows,
    cols,
  };
});

const streamerItemWidth = computed(() => {
  return `${100 / dimensions.value.cols}%`;
});

const streamerItemHeight = computed(() => {
  return `${100 / dimensions.value.rows}%`;
});

function addStreamer () {
  if (name.value !== null && !streamerNames.value.includes(name.value)) {
    streamerNames.value.push(name.value);
    name.value = null;
  }
}

const generateLink = () => {
  if (streamerNames.value.length > 0) {
    const routeData = router.resolve({ name: 'MultiStream', query: { name: streamerNames.value } });
    window.open(routeData.href, '_blank');
    Analytics.event('multistream_generate_link', { streamers: streamerNames.value });
  }
};

function removeStreamer (index) {
  streamerNames.value.splice(index, 1);
}
</script>

<style scoped>
.streams-wrapper {
  margin-top: 10px;
  height: calc(100vh - 110px);
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  gap: 20px;
}

.stream-item {
  flex: 0 1 calc(v-bind(streamerItemWidth) - 10px);
  height: calc(v-bind(streamerItemHeight) - 30px);
}

.stream-wrapper {
  height: 100%;
  display: flex;
  flex-direction: row;
}

.stream-frame-wrapper {
  flex: 0 0 75%;
}

.chat-frame-wrapper {
  flex: 0 0 25%;
  width: 25%;
}

.chat-frame {
  height: calc(100% - 50px);
}
</style>
